import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Scale, Calculator, FileDown, Layers } from 'lucide-react';
import SEO, { organizationSchema, localBusinessSchema, websiteSchema, videoSchema, itemListSchema } from '../components/SEO';
import HeroMedia from '../components/HeroMedia';
import { PartnerWall, IndustryWall, ClientWall } from '../components/home/HomeWalls';
import { PrincipalMills } from '../components/home/PrincipalMills';
import api from '../lib/api';
import { resolveImageUrl } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';
import { imgProps } from '../lib/images';

const EASE = [0.16, 1, 0.3, 1];
const CTA_IMAGE = 'https://images.unsplash.com/photo-1720036237038-802f50cdfd9c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxzdGVlbCUyMHBsYXRlJTIwd2FyZWhvdXNlJTIwZGFyayUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzkwNzU2NTk2fDA&ixlib=rb-4.1.0&q=85';
const MILLS = ['SSAB', 'HARDOX', 'ARMOX', 'STRENX', 'ARCELORMITTAL NIPPON STEEL', 'STANCOR', 'HUNAN VALIN',
  'INCONEL', 'MONEL', 'HASTELLOY', 'ALLEIMA', 'ORBILAN'];

/* Word-by-word headline reveal */
const Reveal = ({ text, className = '', delay = 0, immediate = false }) => (
  <span className={className}>
    {String(text || '').split(' ').map((word, i) => (
      <motion.span
        key={i}
        className="inline-block"
        initial={{ opacity: 0, y: '0.35em' }}
        {...(immediate
          ? { animate: { opacity: 1, y: 0 } }
          : { whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-8%' } })}
        transition={{ duration: 0.85, ease: EASE, delay: delay + i * 0.05 }}
      >
        {word}&nbsp;
      </motion.span>
    ))}
  </span>
);

const Fade = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-10%' }}
    transition={{ duration: 0.8, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

/* Counts up when scrolled into view */
const Counter = ({ value }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-15%' });
  const [shown, setShown] = useState('0');
  const reduce = useReducedMotion();
  const digits = String(value).match(/[\d.]+/);
  const target = digits ? parseFloat(digits[0]) : 0;
  const suffix = digits ? String(value).replace(digits[0], '') : String(value);

  useEffect(() => {
    if (!inView) return;
    if (reduce || !digits) { setShown(String(target)); return; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / 1400, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Number.isInteger(target) ? String(Math.round(target * eased)) : (target * eased).toFixed(1));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, reduce, digits]);

  return <span ref={ref} className="px-data">{shown}{suffix}</span>;
};

/* Numbered family index with a cursor-following preview */
const FamilyIndex = ({ categories }) => {
  const [active, setActive] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const x = useSpring(0, { stiffness: 180, damping: 22 });
  const y = useSpring(0, { stiffness: 180, damping: 22 });

  useEffect(() => { x.set(pos.x); y.set(pos.y); }, [pos, x, y]);

  return (
    <div
      className="relative"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
      }}
      onMouseLeave={() => setActive(null)}
      data-testid="family-index"
    >
      {categories.map((cat, i) => (
        <Fade key={cat.slug} delay={i * 0.04}>
          <Link
            to={`/materials/${cat.slug}`}
            data-testid={`family-row-${cat.slug}`}
            onMouseEnter={() => setActive(cat)}
            className="group grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1fr_auto_auto] items-baseline gap-4 md:gap-10 border-t px-hair py-6 md:py-8"
          >
            <span className="px-data text-xs text-[var(--px-ink-soft)] tabular-nums">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="px-display text-xl md:text-3xl lg:text-4xl font-semibold text-[var(--px-ink)] transition-transform duration-500 ease-out md:group-hover:translate-x-3 rtl:md:group-hover:-translate-x-3">
              {cat.name}
            </span>
            <span className="px-data hidden md:block text-[11px] text-[var(--px-ink-soft)]">
              {cat.grade_count} GRADES
            </span>
            <ArrowUpRight className="w-5 h-5 text-[var(--px-ink-soft)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 rtl-flip" />
          </Link>
        </Fade>
      ))}
      <div className="border-t px-hair" />

      {active && (
        <motion.div
          style={{ x, y }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="pointer-events-none absolute top-0 left-0 hidden lg:block z-20 -translate-x-1/2 -translate-y-1/2"
        >
          <img
            {...imgProps(active.image_url, { width: 680, height: 460, sizes: '340px' })}
            alt=""
            className="w-[340px] h-[230px] object-cover rounded-lg shadow-[0_30px_80px_-30px_rgba(11,14,19,0.5)]"
          />
        </motion.div>
      )}
    </div>
  );
};

export default function Home2030() {
  const { t, lang } = useLanguage();
  const [slides, setSlides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [grades, setGrades] = useState([]);
  const [insights, setInsights] = useState([]);
  const [partners, setPartners] = useState([]);
  const [millsCfg, setMillsCfg] = useState(null);
  const [clients, setClients] = useState([]);
  const [industrial, setIndustrial] = useState([]);
  const [index, setIndex] = useState(0);
  const heroRef = useRef(null);
  const panelRef = useRef(null);
  const panelInView = useInView(panelRef, { margin: '-5%' });

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.14]);
  const heroFade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const heroLift = useTransform(scrollYProgress, [0, 1], [0, -70]);

  useEffect(() => {
    api.get('/hero-slides').then(r => setSlides(r.data || [])).catch(() => {});
    api.get('/catalog/categories').then(r => setCategories(r.data || [])).catch(() => {});
    api.get('/catalog/grades').then(r => setGrades(r.data || [])).catch(() => {});
    api.get('/industrial-products').then(r => setIndustrial(r.data || [])).catch(() => {});
    api.get('/insights?limit=3').then(r => setInsights(r.data || [])).catch(() => {});
    api.get('/settings/public/mills_display').then(r => setMillsCfg(r.data || {})).catch(() => {});
    api.get('/partners/public').then(r => setPartners(r.data || [])).catch(() => {});
    api.get('/clients/public').then(r => setClients(r.data || [])).catch(() => {});
    api.post('/analytics/track', { page: 'home' }).catch(() => {});
  }, [lang]);

  useEffect(() => {
    if (slides.length < 2) return;
    const ms = (Number(slides[index]?.duration) || 7) * 1000;
    const id = setTimeout(() => setIndex(i => (i + 1) % slides.length), ms);
    return () => clearTimeout(id);
  }, [slides, index]);

  const slide = slides[index];
  const millNames = partners.length
    ? partners.map(p => p.name.toUpperCase())
    : MILLS;
  const highlights = (slide?.highlights || []).filter(h => h && (h.value || h.label)).slice(0, 3);
  const gradeTotal = grades.length
    || categories.reduce((sum, c) => sum + (c.grade_count || 0), 0);
  const stats = [
    { value: '2014', label: t('home.statFounded') },
    { value: String(categories.length), label: t('home.statFamilies') },
    { value: String(gradeTotal), label: t('home.statGrades') },
    { value: '40+', label: t('home.statCountries') },
  ];
  const tools = [
    { to: '/compare', icon: Scale, title: t('home.toolCompare'), text: t('home.toolCompareText'), span: 'lg:col-span-3' },
    { to: '/tools/steel-weight-calculator', icon: Calculator, title: t('home.toolCalc'), text: t('home.toolCalcText'), span: 'lg:col-span-3' },
    { to: '/grades', icon: Layers, title: t('home.toolFinder'), text: t('home.toolFinderText'), span: 'lg:col-span-3' },
    { to: '/materials', icon: FileDown, title: t('home.toolSheets'), text: t('home.toolSheetsText'), span: 'lg:col-span-3' },
  ];

  return (
    <div className="bg-[var(--px-paper)] px-body" data-testid="home-2030">
      <SEO
        title="Specialty Steel & High-Performance Alloys — Dubai Stockist"
        description="Dubai stockist of wear plate, stainless, duplex, nickel alloys, pressure vessel plate, line pipe, cupro nickel, titanium and armour plate. EN 10204 3.1 certified."
        canonical="/"
        keywords="steel supplier Dubai, Hardox plate UAE, duplex pipe stockist, nickel alloy supplier Dubai, wear resistant plate GCC, EN 10204 3.1 certified steel"
        jsonLd={[
          organizationSchema,
          localBusinessSchema,
          websiteSchema,
          categories.length > 0
            ? itemListSchema(categories.map(c => ({ name: c.name, url: `/materials/${c.slug}` })), 'Material families stocked by Axicor DWC LLC')
            : null,
          slide?.video_url
            ? videoSchema({
                name: `${slide.title || 'Axicor DWC LLC'} — Axicor DWC LLC`,
                description: slide.caption || slide.subtitle || 'Specialty steel and alloy supply from Dubai.',
                thumbnailUrl: slide.url,
                contentUrl: slide.video_url,
              })
            : null,
        ]}
      />

      {/* ── HERO — full-bleed media with overlaid content ── */}
      <section
        ref={heroRef}
        className="relative h-[100svh] min-h-[620px] overflow-hidden bg-[var(--px-ink)]"
        data-testid="hero-full"
      >
        <div ref={panelRef} className="absolute inset-0">
          <motion.div style={{ scale: heroScale }} className="absolute inset-0">
            {slides.map((s, i) => (
              <motion.div
                key={s.id || i}
                animate={{ opacity: i === index ? 1 : 0 }}
                transition={{ duration: 1.3, ease: EASE }}
                className="absolute inset-0"
                style={{ pointerEvents: 'none' }}
              >
                <HeroMedia slide={s} active={i === index} inView={panelInView} />
              </motion.div>
            ))}
          </motion.div>
          {/* scrim: navy from the reading edge and the base so type always holds */}
          <div className="absolute inset-0 px-scrim-side" />
          <div className="absolute inset-0 px-scrim-base" />
          <div className="absolute inset-0 px-grain" />
        </div>

        <motion.div
          style={{ opacity: heroFade, y: heroLift }}
          className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-between pt-28 pb-10 md:pb-12"
        >
          <div className="flex-1 flex flex-col justify-center max-w-4xl">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.25, duration: 0.8 }}
              className="px-data text-[10px] md:text-[11px] tracking-[0.25em] text-white/55 mb-6"
            >
              [ {t('home.since')} 2014 ] &nbsp; {t('home.kicker')}
            </motion.p>

            <h1 className="px-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] font-extrabold tracking-tight text-white max-w-[20ch]">
              <Reveal immediate text={slide?.title?.replace(/\n/g, ' ') || t('home.heroFallback')} delay={0.35} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8, ease: EASE }}
              className="text-sm md:text-lg text-white/70 mt-7 leading-relaxed max-w-xl"
            >
              {slide?.subtitle || t('home.heroSubFallback')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
              className="flex flex-wrap items-center gap-4 mt-10"
            >
              <Link
                to={slide?.cta_link || '/materials'}
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-3 bg-white text-[var(--px-ink)] rounded px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] hover:bg-[var(--px-accent)] hover:text-white transition-colors duration-500"
              >
                {slide?.cta_text || t('nav.materialsAndGrades')}
                <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1.5 rtl-flip" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-quote-button"
                className="inline-flex items-center gap-2 rounded px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white border border-white/30 hover:border-white hover:bg-white/10 transition-colors duration-500"
              >
                {t('common.requestQuote')}
              </Link>
            </motion.div>

            {highlights.length > 0 && (
              <div className="flex flex-wrap gap-x-12 gap-y-5 mt-12 md:mt-16" data-testid="hero-highlights">
                {highlights.map((hl, i) => (
                  <motion.div
                    key={`${hl.label}-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.95 + i * 0.1 }}
                    className="border-s border-white/20 ps-4"
                  >
                    <p className="px-display text-2xl md:text-4xl font-bold text-white leading-none">{hl.value}</p>
                    <p className="px-data text-[10px] tracking-[0.2em] text-white/50 mt-2 uppercase">{hl.label}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-end justify-between gap-6 border-t border-white/15 pt-5">
            <span className="px-data text-[10px] tracking-[0.25em] text-white/50 hidden sm:block">
              {t('home.scroll')}
            </span>
            <span className="px-data text-[10px] tracking-[0.22em] text-white/60 truncate">
              {slide?.caption || t('home.heroPanelCaption')}
            </span>
            <div className="flex items-center gap-4 shrink-0">
              <div className="flex gap-2" data-testid="hero-dots">
                {slides.map((s, i) => (
                  <button
                    key={s.id || i}
                    onClick={() => setIndex(i)}
                    data-testid={`hero-dot-${i}`}
                    aria-label={`Slide ${i + 1}`}
                    className={`h-[2px] transition-all duration-700 ${i === index ? 'w-10 bg-white' : 'w-5 bg-white/35'}`}
                  />
                ))}
              </div>
              <span className="px-data text-[10px] tracking-[0.22em] text-white/45 tabular-nums">
                {String(index + 1).padStart(2, '0')} / {String(Math.max(slides.length, 1)).padStart(2, '0')}
              </span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── MILL MARQUEE ─────────────────────────────────── */}
      <section className="border-y px-hair bg-[var(--px-surface)] py-5 overflow-hidden" data-testid="mill-marquee">
        <div className="px-marquee-track">
          {[0, 1].map(dup => (
            <div key={dup} className="flex shrink-0">
              {millNames.map(m => (
                <span key={`${dup}-${m}`} className="px-data text-[11px] tracking-[0.3em] text-[var(--px-ink-soft)] px-8 whitespace-nowrap">
                  {m}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────── */}
      <section className="bg-[var(--px-surface)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-[var(--px-line)] border-b px-hair">
          {(categories.length ? stats : []).map((s, i) => (
            <Fade key={s.label} delay={i * 0.07}>
              <div className="px-2 py-12 md:py-16">
                <p className="px-display text-4xl md:text-6xl font-extrabold text-[var(--px-ink)] tabular-nums">
                  <Counter value={s.value} />
                </p>
                <p className="px-data text-[10px] tracking-[0.22em] text-[var(--px-ink-soft)] mt-3">{s.label}</p>
              </div>
            </Fade>
          ))}
        </div>
      </section>

      {millsCfg?.homepage_tier === false ? (
        <PartnerWall partners={partners} />
      ) : (
        <PrincipalMills mills={partners.filter(p => p.principal)} heading={millsCfg?.heading} />
      )}

      <ClientWall clients={clients} />

      <IndustryWall />

      {/* ── FAMILY INDEX ─────────────────────────────────── */}
      <section className="relative bg-[var(--px-surface)] py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 lg:gap-24 items-end mb-12 md:mb-16">
            <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)]">[ 03 ] {t('home.indexKicker')}</p>
            <h2 className="px-display text-3xl md:text-5xl lg:text-[3.4rem] leading-[1.05] font-semibold text-[var(--px-ink)] max-w-3xl">
              <Reveal text={t('home.indexHeading')} />
            </h2>
          </div>
          <FamilyIndex categories={categories} />
        </div>
      </section>

      {/* ── EDITORIAL STATEMENT ──────────────────────────── */}
      <section className="bg-[var(--px-ink)] py-24 md:py-36 relative overflow-hidden px-grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="px-data text-[11px] tracking-[0.25em] text-white/40 mb-10">[ 04 ] {t('home.promiseKicker')}</p>
          {[t('home.promise1'), t('home.promise2'), t('home.promise3')].map((word, i) => (
            <div key={i} className="overflow-hidden">
              <motion.p
                initial={{ y: '100%', opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: '-15%' }}
                transition={{ duration: 1, ease: EASE, delay: i * 0.12 }}
                className="px-display text-[13vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.95] font-extrabold text-white"
              >
                {word}
              </motion.p>
            </div>
          ))}
          <Fade delay={0.4}>
            <p className="text-sm md:text-base text-white/55 mt-10 max-w-xl leading-relaxed">{t('home.promiseText')}</p>
          </Fade>
        </div>
      </section>

      {/* ── TOOL BENTO ───────────────────────────────────── */}
      <section className="bg-[var(--px-surface)] py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-12 md:mb-16">
            <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)] mb-6">[ 05 ] {t('home.toolsKicker')}</p>
            <h2 className="px-display text-3xl md:text-5xl lg:text-[3.4rem] leading-[1.05] font-semibold text-[var(--px-ink)] max-w-3xl">
              <Reveal text={t('home.toolsHeading')} />
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4" data-testid="tool-bento">
            {tools.map((tool, i) => (
              <Fade key={tool.to} delay={i * 0.08} className={tool.span}>
                <Link
                  to={tool.to}
                  data-testid={`tool-card-${tool.to.split('/').pop()}`}
                  className="px-lift group relative h-full flex flex-col justify-between bg-[var(--px-paper)] border px-hair rounded-lg p-7 md:p-9 overflow-hidden"
                >
                  <span className="absolute inset-x-0 top-0 h-[2px] bg-[var(--px-accent)] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-700" />
                  <tool.icon className="w-6 h-6 text-[var(--px-accent)]" strokeWidth={1.5} />
                  <div className="mt-10">
                    <h3 className="px-display text-xl md:text-2xl font-semibold text-[var(--px-ink)]">{tool.title}</h3>
                    <p className="text-sm text-[var(--px-ink-soft)] mt-3 leading-relaxed max-w-md">{tool.text}</p>
                    <span className="px-data inline-flex items-center gap-2 text-[10px] tracking-[0.2em] text-[var(--px-ink)] mt-6">
                      {t('common.readMore')}
                      <ArrowRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1.5 rtl-flip" />
                    </span>
                  </div>
                </Link>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── GRADE RAIL ───────────────────────────────────── */}
      <section className="bg-[var(--px-paper)] py-20 md:py-32 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)] mb-6">[ 06 ] {t('home.gradesKicker')}</p>
            <h2 className="px-display text-3xl md:text-5xl font-semibold text-[var(--px-ink)]">
              <Reveal text={t('home.gradesHeading')} />
            </h2>
          </div>
          <Link to="/materials" data-testid="view-all-products-link" className="px-data hidden md:inline-flex items-center gap-2 text-[10px] tracking-[0.2em] text-[var(--px-ink)] border-b border-[var(--px-ink)] pb-1">
            {t('nav.materialsAndGrades')} <ArrowRight className="w-3.5 h-3.5 rtl-flip" />
          </Link>
        </div>

        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-6 lg:px-10 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="grade-rail">
          {grades.slice(0, 10).map((g, i) => (
            <Fade key={g.slug} delay={Math.min(i, 5) * 0.06}>
              <Link
                to={`/materials/${g.category_slug}/${g.slug}`}
                data-testid={`featured-product-${i}`}
                className="group snap-start block w-[78vw] sm:w-[340px] shrink-0"
              >
                <div className="relative overflow-hidden rounded-lg aspect-[4/5] bg-[var(--px-ink)]">
                  <img
                    src={resolveImageUrl(g.image_url)}
                    alt={g.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.07]"
                  />
                  <span className="px-data absolute top-4 left-4 text-[10px] tracking-[0.2em] text-white/90 bg-black/35 backdrop-blur-md rounded-full px-3 py-1.5">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="grade-name px-display text-lg md:text-xl font-semibold text-[var(--px-ink)] mt-5">{g.name}</h3>
                <p className="text-sm text-[var(--px-ink-soft)] mt-2 line-clamp-2 leading-relaxed">{g.description}</p>
              </Link>
            </Fade>
          ))}
        </div>
      </section>

      {/* ── INDUSTRIAL PRODUCTS ──────────────────────────── */}
      {industrial.length > 0 && (
        <section className="bg-[var(--px-surface)] py-20 md:py-28 border-y px-hair" data-testid="home-industrial">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-14">
              <div className="max-w-2xl">
                <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)] mb-6">
                  [ 07 ] {t('products.homeKicker')}
                </p>
                <h2 className="px-display text-3xl md:text-5xl font-semibold text-[var(--px-ink)] leading-tight">
                  <Reveal text={t('products.homeHeading')} />
                </h2>
                <p className="text-sm md:text-base text-[var(--px-ink-soft)] mt-5 leading-relaxed">
                  {t('products.homeLead')}
                </p>
              </div>
              <Link
                to="/products"
                data-testid="home-all-products-link"
                className="px-data hidden md:inline-flex items-center gap-2 text-[10px] tracking-[0.2em] text-[var(--px-ink)] border-b border-[var(--px-ink)] pb-1"
              >
                {t('products.viewAll')} <ArrowRight className="w-3.5 h-3.5 rtl-flip" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {industrial.slice(0, 3).map((p, i) => (
                <Fade key={p.slug} delay={i * 0.08}>
                  <Link
                    to={`/products/${p.slug}`}
                    data-testid={`home-industrial-${p.slug}`}
                    className="group block h-full bg-[var(--px-paper)] border px-hair hover:border-[var(--px-accent)] transition-colors duration-500 overflow-hidden"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-[var(--px-ink)]">
                      <img
                        src={resolveImageUrl(p.image_url)}
                        alt={p.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.06]"
                      />
                      <span className="px-data absolute top-4 left-4 text-[10px] tracking-[0.2em] text-white/90 bg-black/40 backdrop-blur-md rounded-full px-3 py-1.5">
                        {String(i + 9).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="p-6">
                      <h3 className="px-display text-lg md:text-xl font-semibold text-[var(--px-ink)]">{p.name}</h3>
                      <p className="text-sm text-[var(--px-ink-soft)] mt-2.5 line-clamp-3 leading-relaxed">
                        {p.tagline || p.description}
                      </p>
                      <span className="px-data inline-flex items-center gap-2 mt-5 text-[10px] uppercase tracking-[0.18em] text-[var(--px-accent)]">
                        {(p.types || []).length} {t('products.typesCount')} <ArrowRight className="w-3 h-3 rtl-flip" />
                      </span>
                    </div>
                  </Link>
                </Fade>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── INSIGHTS + CTA ───────────────────────────────── */}
      <section className="bg-[var(--px-surface)] py-20 md:py-32 border-t px-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {insights.length > 0 && (
            <>
              <div className="flex items-end justify-between gap-6 mb-10">
                <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)]">[ 07 ] {t('home.insightsKicker')}</p>
                <Link to="/insights" data-testid="all-insights-link" className="px-data hidden md:inline-flex items-center gap-2 text-[10px] tracking-[0.2em] text-[var(--px-ink)] border-b border-[var(--px-ink)] pb-1">
                  {t('nav.insights')} <ArrowRight className="w-3.5 h-3.5 rtl-flip" />
                </Link>
              </div>
              <div
                className={`grid grid-cols-1 gap-8 mb-24 ${insights.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}
                data-testid="home-insights"
              >
                {insights.slice(0, 3).map((a, i) => (
                  <Fade key={a.slug} delay={i * 0.08}>
                    <Link to={`/insights/${a.slug}`} data-testid={`home-insight-${i}`} className="group block h-full">
                      {a.image_url && (
                        <div className="relative overflow-hidden rounded-lg aspect-[16/10] bg-[var(--px-ink)] mb-6">
                          <img
                            src={resolveImageUrl(a.image_url)}
                            alt={a.title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.06]"
                          />
                        </div>
                      )}
                      <div className="border-t px-hair pt-6">
                        <span className="px-data text-[10px] tracking-[0.2em] text-[var(--px-ink-soft)]">
                          {String(i + 1).padStart(2, '0')} &nbsp;/&nbsp; {a.category || 'TECHNICAL'}
                        </span>
                        <h3 className="px-display text-lg md:text-2xl font-semibold text-[var(--px-ink)] mt-4 leading-snug group-hover:text-[var(--px-accent)] transition-colors duration-500">
                          {a.title}
                        </h3>
                        <p className="text-sm text-[var(--px-ink-soft)] mt-3 line-clamp-3 leading-relaxed">{a.dek}</p>
                      </div>
                    </Link>
                  </Fade>
                ))}

                {insights.length < 3 && (
                  <Fade delay={0.12}>
                    <Link
                      to="/insights"
                      data-testid="insights-archive-tile"
                      className="px-lift group flex h-full min-h-[280px] flex-col justify-between rounded-lg border px-hair bg-[var(--px-paper)] p-8 md:p-10"
                    >
                      <span className="px-data text-[10px] tracking-[0.2em] text-[var(--px-ink-soft)]">
                        {t('home.insightsKicker')}
                      </span>
                      <div>
                        <h3 className="px-display text-xl md:text-3xl font-semibold text-[var(--px-ink)] leading-tight">
                          {t('home.insightsArchive')}
                        </h3>
                        <span className="px-data inline-flex items-center gap-2 text-[10px] tracking-[0.2em] text-[var(--px-ink)] mt-6">
                          {t('common.readMore')}
                          <ArrowRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1.5 rtl-flip" />
                        </span>
                      </div>
                    </Link>
                  </Fade>
                )}
              </div>
            </>
          )}

          <Fade>
            <div
              className="relative overflow-hidden rounded-xl bg-[var(--px-ink)] px-watermark"
              style={{ '--px-mark': "url('/brand/axicor-mark-white.png')" }}
              data-testid="home-cta-band"
            >
              <img
                src={CTA_IMAGE}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 px-scrim-cta" />
              <div className="absolute inset-0 px-grain" />

              <div className="relative p-10 md:p-20">
                <p className="px-data text-[11px] tracking-[0.25em] text-white/45 mb-8">[ 08 ] {t('common.requestQuote')}</p>
                <h2 className="px-display text-3xl md:text-5xl lg:text-[3.6rem] leading-[1.05] font-semibold text-white max-w-3xl">
                  <Reveal text={t('home.ctaHeading')} />
                </h2>
                <p className="text-sm md:text-base text-white/60 mt-6 max-w-xl leading-relaxed">{t('home.ctaText')}</p>
                <Link
                  to="/contact"
                  data-testid="home-cta-contact"
                  className="group inline-flex items-center gap-3 bg-white text-[var(--px-ink)] rounded px-8 py-4 mt-10 text-xs font-bold uppercase tracking-[0.18em] hover:bg-[var(--px-accent)] hover:text-white transition-colors duration-500"
                >
                  {t('common.requestQuote')}
                  <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1.5 rtl-flip" />
                </Link>

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-white/10 pt-8 max-w-3xl">
                  {[
                    { k: t('home.ctaRail1Label'), v: t('home.ctaRail1Value') },
                    { k: t('home.ctaRail2Label'), v: t('home.ctaRail2Value') },
                    { k: t('home.ctaRail3Label'), v: t('home.ctaRail3Value') },
                  ].map(item => (
                    <div key={item.k}>
                      <p className="px-data text-[10px] tracking-[0.22em] text-white/40">{item.k}</p>
                      <p className="px-data text-sm text-white mt-2">{item.v}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Fade>
        </div>
      </section>
    </div>
  );
}
